import * as ngCore from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { pack } from '@attain-apps/data-utils'
import * as errors from '@attain-apps/errors'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as models from '../models'

import type { Collection } from './collection'
import type { Name } from './name'

type Filters<TName extends Name> = ExactlyOneOf<{
  names: readonly TName[]
  isName: (name: TName) => boolean
}>
namespace Filters {
  /**
   * Filters the given entity name based on the provided filters.
   */
  export function filter<TName extends Name>({
    name,
    filters: { names = [], isName },
  }: {
    filters: Filters<TName>
    name: TName
  }): boolean {
    if (isName) {
      return isName(name)
    }

    if (names.length > 0) {
      return names.includes(name)
    }

    return false
  }
}

/**
 *  Manages all instances of entity collections
 *
 * @internal
 * @see {@link Collection.instances}
 */
export class InstanceManager {
  /**
   * Holds all instance of entity collections, used to get entity collection
   * instance by entityName
   */
  private readonly _instanceLookup$: rxjs.BehaviorSubject<
    Partial<Record<Name, Collection<models.TypeParams>>>
  > = new rxjs.BehaviorSubject({})

  /** Signal version of {@link _instanceLookup$} */
  private readonly _instanceLookupSg = toSignal(this._instanceLookup$, {
    // Indicates to signal that it will be destroyed, after observable is completed.
    manualCleanup: true,
    // Obtains initial value from the observable, otherwise, this signal would
    // start as "undefined"
    requireSync: true,
  })

  /**
   * Registers an instance so that it can be retrieved later
   */
  public register<TTypeParams extends models.TypeParams.Constraint>(
    instance: Collection<TTypeParams>,
  ): void {
    const instances = this._instanceLookup$.getValue()
    this._instanceLookup$.next({
      ...instances,
      [instance.name]: instance,
    })
  }

  /**
   * Unregister an instance so that it can no longer be retrieved
   */
  public unregister<TTypeParams extends models.TypeParams.Constraint>(
    instance: Collection<TTypeParams>,
  ): void {
    const instances = this._instanceLookup$.getValue()
    this._instanceLookup$.next({
      ...instances,
      [instance.name]: undefined,
    })
  }

  /**
   * Retrieves all instances of entity collections that match the given {@link Filters}.
   */
  public find<TName extends Name>(filters: Filters<TName>) {
    // store instance of `this` so we can use it in the getter
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this

    // We use a local variable to cache the observable and signal, so that we
    // don't create a new one every time getter is called
    let instances$: rxjs.Observable<Collection<models.TypeParams>[]> | undefined
    let instancesSg: ngCore.Signal<Collection<models.TypeParams>[]> | undefined

    return {
      get asObservable() {
        return (instances$ ??= _this._instanceLookup$.pipe(
          rxjs.switchMap(instanceLookup => {
            const instancesWithPluginsReady$: rxjs.Observable<
              Collection<models.TypeParams> | undefined
            >[] = []
            for (const key in instanceLookup) {
              // NOTE: typescript is not intelligent enough to infer that
              const instanceName = key as TName
              const instance = instanceLookup[instanceName]
              if (
                // If filters are provided, we check if the instanceName matches
                // the filters, if not skip iteration
                !Filters.filter<TName>({ filters, name: instanceName }) ||
                // If instance is not defined, we skip iteration
                !instance
              ) {
                continue
              }

              // collect observables of instances that have all plugins instantiated
              instancesWithPluginsReady$.push(
                // NOTE: We should wait until at least collection's plugins
                // have been instantiated, as that avoids issues when trying
                // to reference the collection's plugins
                instance.plugins.feature.selectors.allPluginsHaveBeenInstantiated.asObservable.pipe(
                  rxjs.map(allPluginsHaveBeenInstantiated =>
                    // If all plugins of given collection have been
                    // instantiated, we can return the instance, otherwise
                    // we return undefined
                    allPluginsHaveBeenInstantiated ? instance : undefined,
                  ),
                ),
              )
            }

            return instancesWithPluginsReady$.length > 0
              ? rxjs.combineLatest(instancesWithPluginsReady$)
              : rxjs.of([])
          }),
          rxjs.map(results =>
            // filter out instances that don't have all plugins instantiate
            results.filter(TypeChecker.isNotNullish.raw),
          ),
        ))
      },
      get asSignal() {
        return (instancesSg ??= ngCore.computed(
          (instanceLookup = _this._instanceLookupSg()) => {
            const instancesWithPluginsReady: Collection<models.TypeParams>[] =
              []

            for (const key in instanceLookup) {
              // NOTE: typescript is not intelligent enough to infer that
              const instanceName = key as TName
              const instance = instanceLookup[instanceName]

              if (
                // If filters are provided, we check if the instanceName matches
                // the filters, if not skip iteration
                !Filters.filter({ filters, name: instanceName }) ||
                // If instance is not defined, we skip iteration
                !instance
              ) {
                continue
              }

              if (
                // NOTE: We should wait until at least collection's plugins
                // have been instantiated, as that avoids issues when trying
                // to reference the collection's plugins
                instance.plugins.feature.selectors.allPluginsHaveBeenInstantiated.asSignal()
              ) {
                instancesWithPluginsReady.push(instance)
              }
            }

            return instancesWithPluginsReady
          },
        ))
      },
      get collections() {
        const instances: Collection<models.TypeParams>[] = []
        const instanceLookup = _this._instanceLookupSg()

        for (const key in instanceLookup) {
          // NOTE: typescript is not intelligent enough to infer that
          const instanceName = key as TName
          const instance = instanceLookup[instanceName]

          if (
            // If filters are provided, we check if the instanceName matches
            // the filters, if not skip iteration
            !Filters.filter({ filters, name: instanceName }) ||
            // If instance is not defined, we skip iteration
            !instance
          ) {
            continue
          }

          instances.push(instance)
        }

        return instances
      },
    }
  }

  /**
   * Gets an entity collection by name. Returns undefined if not found.
   */
  public tryGet<TTypeParams extends models.TypeParams.Constraint>(name: Name) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this

    return {
      get asObservable() {
        return _this
          .find({
            names: [name],
          })
          .asObservable.pipe(
            rxjs.map(
              ([collection]) =>
                // TODO: how to avoid casting to unknown?
                collection satisfies
                  | typeof collection
                  | undefined as unknown as Collection<TTypeParams> | undefined,
            ),
          )
      },
      get asSignal() {
        return ngCore.computed(
          ([collection] = _this.find({ names: [name] }).asSignal()) =>
            // TODO: how to avoid casting to unknown?
            collection satisfies typeof collection | undefined as unknown as
              | Collection<TTypeParams>
              | undefined,
        )
      },
      get collection() {
        const [collection] = _this.find({
          names: [name],
        }).collections
        return collection satisfies typeof collection | undefined as unknown as
          | Collection<TTypeParams>
          | undefined
      },
    }
  }

  /**
   * Gets an entity collection by name. For signals and sync access, throws
   * {@link errors.NotFoundError} when collection is not found. The observable
   * getter waits for collection availability.
   */
  public get<TTypeParams extends models.TypeParams.Constraint>(name: Name) {
    const { asObservable, asSignal, collection } =
      this.tryGet<TTypeParams>(name)
    let _signal: ngCore.Signal<Collection<TTypeParams>> | undefined

    /** TODO:  */
    const notFoundError = new errors.NotFoundError({
      id: `for ${name}`,
      location: 'the set of currently instantiated entity collection',
      type: 'entity collection',
    })

    return {
      /** Similar to {@link find} version, emits only when {@link Collection} is found */
      get asObservable() {
        // do not emit values until the collection is available
        return asObservable.pipe(rxjs.filter(TypeChecker.isNotNullish.raw))
      },
      /** Similar to {@link find} version, but throws error if {@link Collection} is not found */
      // TODO: Not sure if we even need this case, but for consistency we keep it
      get asSignal() {
        return (_signal ??= ngCore.computed((col = asSignal()) => {
          if (col === undefined) {
            throw notFoundError
          }
          return col
        }))
      },
      /** Similar to {@link find} version, but throws error if {@link Collection} is not found */
      get collection() {
        if (collection === undefined) {
          throw notFoundError
        }

        return collection
      },
    }
  }
}
